import { useCallback, useEffect, useState } from 'preact/hooks';
import type { Overview, SeriesPoint } from '../../shared/types';
import { apiGet, apiPost } from '../lib/api';
import { useTimeWindow } from '../lib/contexts';
import { TopBar } from '../components/TopBar';
import { Hero } from '../components/Hero';
import { PoolCard } from '../components/PoolCard';
import { DeliveryBanner, WatchdogBanner } from '../components/WatchdogBanner';
import { WarningsStrip } from '../components/WarningsStrip';
import { AlertsFeed } from '../components/AlertsFeed';
import { PositionsSection } from '../components/PositionsSection';
import { HeroSkeleton, GridSkeleton } from '../components/Skeleton';
import '../styles/components/dashboard.css';

export function DashboardPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { days } = useTimeWindow();
  // Sparkline series for the 30/90 d windows; the 7 d series ships with the overview.
  const [longSeries, setLongSeries] = useState<Record<string, SeriesPoint[]> | null>(null);

  const load = useCallback(() => apiGet<Overview>('/api/overview').then(setData), []);
  useEffect(() => {
    load().catch((e: Error) => setError(e.message));
  }, [load]);

  useEffect(() => {
    if (!data || days === 7) {
      setLongSeries(null);
      return;
    }
    let cancelled = false;
    Promise.all(
      data.pools
        .filter((p) => p.pool.kind !== 'eth-gas')
        .map((p) => apiGet<{ points: SeriesPoint[] }>(`/api/pools/${p.pool.id}/history?days=${days}`).then((r) => [p.pool.id, r.points] as const))
    )
      .then((rows) => { if (!cancelled) setLongSeries(Object.fromEntries(rows)); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [data, days]);

  const runNow = async () => {
    await apiPost('/api/admin/run?kind=hourly', {});
    await load();
  };
  // 502 (no channel accepted) surfaces as the thrown error body; 200 lists every channel's outcome.
  const testNotify = async () => {
    const r = await apiPost<Record<string, string>>('/api/admin/test-notify', {});
    return Object.entries(r).map(([channel, result]) => `${channel}: ${result}`).join(' · ');
  };

  if (!data && !error) {
    return (
      <main class="dash">
        <TopBar lastRunAt={null} onRun={runNow} onTestNotify={testNotify} />
        <HeroSkeleton />
        <GridSkeleton />
      </main>
    );
  }

  const gas = data?.pools.find((p) => p.pool.kind === 'eth-gas')?.latest;
  const alertsFor = (poolId: string) => data?.activeAlerts.filter((a) => a.poolId === poolId) ?? [];
  const venues = data?.pools.filter((p) => p.pool.kind !== 'eth-gas') ?? [];
  const held = venues.filter((p) => !p.pool.watchOnly);
  const watched = venues.filter((p) => p.pool.watchOnly);
  const watchedIds = new Set(watched.map((p) => p.pool.id));
  const now = data?.generatedAt ?? Math.floor(Date.now() / 1000);
  const lastRunAt = data?.lastRun ? data.lastRun.finishedAt ?? data.lastRun.startedAt : null;
  const withWindow = (item: Overview['pools'][number]) => (longSeries?.[item.pool.id] ? { ...item, series: longSeries[item.pool.id]! } : item);

  return (
    <main class="dash">
      <TopBar lastRunAt={lastRunAt} onRun={runNow} onTestNotify={testNotify} />

      {error && <div class="banner banner--critical" role="alert">{error}</div>}
      {data && <WatchdogBanner lastRun={data.lastRun} now={now} />}
      {data && <DeliveryBanner delivery={data.delivery} now={now} />}

      {data && <Hero pools={held} watched={watched} alerts={data.activeAlerts.filter((a) => !watchedIds.has(a.poolId))} gasGwei={gas?.price ?? null} gasWarn={alertsFor('eth-gas').length > 0} />}

      <div class="pool-grid">{held.map((item) => <PoolCard key={item.pool.id} item={withWindow(item)} alerts={alertsFor(item.pool.id)} />)}</div>
      {watched.length > 0 && (
        <section class="watch-section">
          <h2 class="watch-title">Watching <span class="watch-note">contagion signals only · no Matrix alerts</span></h2>
          <div class="pool-grid">{watched.map((item) => <PoolCard key={item.pool.id} item={withWindow(item)} alerts={alertsFor(item.pool.id)} />)}</div>
        </section>
      )}
      {data && <PositionsSection overview={data} />}
      {data && <AlertsFeed alerts={data.alerts} pools={data.pools.map((p) => p.pool)} />}
      {data?.lastRun && <WarningsStrip warnings={data.lastRun.warnings} />}
    </main>
  );
}
